<template>
  <Dialog
    :visible="visible"
    modal
    :closable="true"
    :dismissableMask="true"
    :draggable="false"
    :style="{ width: '520px' }"
    :breakpoints="{ '640px': '95vw' }"
    @update:visible="onDialogVisibleChange"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <span class="material-icons !text-3xl" aria-hidden="true">support_agent</span>
        <span>{{ $t('botEditor.connectors.livechat.dialogTitle') }}</span>
      </div>
    </template>

    <Divider class="divider-below" />

    <div class="flex flex-col gap-5">
      <Message v-if="statusLoading" severity="secondary" variant="simple" size="small" :closable="false">
        {{ $t('common.loading') }}
      </Message>

      <!-- Connettore non disponibile (admin) o stato non leggibile -->
      <Message v-else-if="loadError" severity="warn" :closable="false">
        {{ loadError }}
      </Message>

      <!-- Acceso: team collegato, apertura Live Chat -->
      <template v-else-if="isActive">
        <div class="flex flex-col gap-1">
          <span class="text-sm text-gray-400">{{ $t('botEditor.connectors.livechat.linkedTeam') }}</span>
          <span v-if="status.teamName || linkedTeamName" class="font-medium">{{ status.teamName || linkedTeamName }}</span>
          <span v-else class="font-mono text-sm break-all">{{ status.handoverTeamId }}</span>
        </div>

        <Message v-if="justActivated" severity="info" :closable="false">
          {{ $t('botEditor.connectors.livechat.publishHint') }}
        </Message>

        <div v-if="openUrl">
          <!-- Link in stile bottone: apre l'handover in una nuova scheda -->
          <a
            :href="openUrl"
            target="_blank"
            rel="noopener"
            class="p-button p-component p-button-secondary p-button-outlined no-underline inline-flex items-center gap-2"
          >
            <span class="material-icons" aria-hidden="true">open_in_new</span>
            {{ $t('botEditor.connectors.livechat.open') }}
          </a>
        </div>
      </template>

      <!-- Spento: scelta del team e attivazione -->
      <template v-else>
        <Message v-if="teams.length === 0" severity="secondary" variant="simple" :closable="false">
          {{ $t('botEditor.connectors.livechat.newTeamInfo', { name: botName }) }}
        </Message>
        <div v-else class="flex flex-col gap-2">
          <label for="livechat-team" id="livechat-team-label" class="font-medium">{{ $t('botEditor.connectors.livechat.teamLabel') }}</label>
          <Select
            label-id="livechat-team"
            aria-labelledby="livechat-team-label"
            v-model="selectedTeamId"
            :options="teamOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
          />
        </div>
      </template>
    </div>

    <template #footer>
      <div class="flex items-center justify-between gap-2">
        <div>
          <Button
            v-if="isActive && !loadError"
            severity="danger"
            outlined
            :label="$t('botEditor.connectors.livechat.deactivate')"
            :loading="actionLoading"
            :disabled="actionLoading"
            @click="askDeactivate"
          >
            <template #icon>
              <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">link_off</span>
            </template>
          </Button>
        </div>
        <div class="flex gap-2">
          <Button
            :label="isActive ? $t('common.close') : $t('botEditor.connectors.configModal.cancelButton')"
            severity="secondary"
            @click="closeDialog"
          />
          <Button
            v-if="!statusLoading && !loadError && !isActive"
            severity="primary"
            :label="$t('botEditor.connectors.livechat.activate')"
            :loading="actionLoading"
            :disabled="actionLoading || teamsLoading"
            @click="activate"
          >
            <template #icon>
              <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">support_agent</span>
            </template>
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { getLiveChatStatus, getLinkableTeams, activateLiveChat, deactivateLiveChat } from '../../services/liveChatApi';
import { liveChatUrl } from '../../utils/liveChatUrl';

const props = defineProps({
  visible: { type: Boolean, default: false },
  workspaceId: { type: String, required: true },
  botId: { type: String, required: true },
  botName: { type: String, default: '' },
  connector: { type: Object, default: null },
});
const emit = defineEmits(['update:visible', 'saved']);

const { t } = useI18n();
const toast = useToast();
const confirm = useConfirm();

const statusLoading = ref(false);
const teamsLoading = ref(false);
const actionLoading = ref(false);
const loadError = ref('');
const status = ref({ enabled: false, handoverTeamId: null, handoverUrl: null });
const teams = ref([]);
// PrimeVue Select tratta null come "nessuna selezione" (campo vuoto): il nuovo
// team usa un valore sentinella, tradotto in "nessun teamId" all'attivazione.
const NEW_TEAM = '__new__';
const selectedTeamId = ref(NEW_TEAM);
const linkedTeamName = ref(''); // noto solo dopo un'attivazione in questa sessione
const justActivated = ref(false);

const isActive = computed(() => status.value.enabled === true);
const openUrl = computed(() => liveChatUrl(status.value.handoverUrl, status.value.handoverTeamId));
const teamOptions = computed(() => [
  { label: t('botEditor.connectors.livechat.newTeamOption', { name: props.botName }), value: NEW_TEAM },
  ...teams.value.map((team) => ({ label: team.name, value: team._id })),
]);

// Traduce l'errore del backend (error.response.data.error.code) in un testo localizzato.
function errorMessage(error) {
  const status = error?.response?.status;
  const body = error?.response?.data?.error;
  switch (body?.code) {
    case 'BOT_ALREADY_LINKED':
      return t('botEditor.connectors.livechat.errors.alreadyLinked', { team: body.teamName || '' });
    case 'CONNECTOR_UNAVAILABLE':
      return t('botEditor.connectors.unavailableError');
    case 'HANDOVER_UNREACHABLE':
      return t('botEditor.connectors.livechat.errors.unreachable');
    case 'HANDOVER_AUTH_FAILED':
      return t('botEditor.connectors.livechat.errors.authFailed');
    default:
      break;
  }
  if (status === 403) return t('botEditor.connectors.livechat.errors.forbidden');
  return body?.message || t('common.error');
}

// `silent`: ricarica dopo attiva/disattiva senza far lampeggiare il messaggio di
// caricamento al posto del contenuto appena aggiornato.
async function loadStatus({ silent = false } = {}) {
  if (!silent) statusLoading.value = true;
  loadError.value = '';
  try {
    status.value = await getLiveChatStatus(props.workspaceId, props.botId, { withTeamName: true });
    if (!status.value.enabled) await loadTeams();
  } catch (error) {
    Console.error('[LiveChatConnectorDialog] Error loading status:', error);
    loadError.value = errorMessage(error);
  } finally {
    statusLoading.value = false;
  }
}

async function loadTeams() {
  teamsLoading.value = true;
  selectedTeamId.value = NEW_TEAM;
  try {
    const data = await getLinkableTeams(props.workspaceId, props.botId);
    teams.value = data?.teams || [];
  } catch (error) {
    // Senza l'elenco si può comunque creare un team nuovo: errore solo nel log + toast
    Console.error('[LiveChatConnectorDialog] Error loading teams:', error);
    teams.value = [];
    toast.add({ severity: 'warn', summary: errorMessage(error), life: 5000 });
  } finally {
    teamsLoading.value = false;
  }
}

watch(() => props.visible, (isVisible) => {
  if (isVisible) {
    justActivated.value = false;
    linkedTeamName.value = '';
    loadStatus();
  }
});

function onDialogVisibleChange() {
  closeDialog();
}

function closeDialog() {
  emit('update:visible', false);
}

async function activate() {
  actionLoading.value = true;
  try {
    const data = await activateLiveChat(props.workspaceId, props.botId, selectedTeamId.value === NEW_TEAM ? undefined : selectedTeamId.value);
    linkedTeamName.value = data?.team?.name || '';
    justActivated.value = true;
    toast.add({ severity: 'success', summary: t('botEditor.connectors.livechat.activated'), life: 3000 });
    await loadStatus({ silent: true });
    emit('saved');
  } catch (error) {
    Console.error('[LiveChatConnectorDialog] Error activating:', error);
    toast.add({ severity: 'error', summary: t('botEditor.connectors.toastError'), detail: errorMessage(error), life: 6000 });
  } finally {
    actionLoading.value = false;
  }
}

// Conferma tramite il ConfirmDialog globale (App.vue)
function askDeactivate() {
  confirm.require({
    message: t('botEditor.connectors.livechat.deactivateConfirm'),
    header: t('botEditor.connectors.livechat.deactivate'),
    icon: 'warning',
    rejectProps: { label: t('botEditor.connectors.configModal.cancelButton'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('botEditor.connectors.livechat.deactivate'), severity: 'danger' },
    accept: deactivate,
  });
}

async function deactivate() {
  actionLoading.value = true;
  try {
    const data = await deactivateLiveChat(props.workspaceId, props.botId);
    justActivated.value = false;
    linkedTeamName.value = '';
    toast.add({ severity: 'success', summary: t('botEditor.connectors.livechat.deactivated'), life: 3000 });
    // 'failed': collegamento tolto nell'handover ma stato locale non confermato.
    // 'local': già scollegato nell'handover, spento solo qui = successo.
    if (data?.crafterSync === 'failed') {
      toast.add({ severity: 'warn', summary: t('botEditor.connectors.livechat.syncFailed'), life: 8000 });
    }
    await loadStatus({ silent: true });
    emit('saved');
  } catch (error) {
    Console.error('[LiveChatConnectorDialog] Error deactivating:', error);
    toast.add({ severity: 'error', summary: t('botEditor.connectors.toastError'), detail: errorMessage(error), life: 6000 });
  } finally {
    actionLoading.value = false;
  }
}
</script>
